import React, { useState, useEffect } from 'react';
import { message, Card, Button, Upload } from 'antd';
import { DownloadOutlined, UploadOutlined, PlusOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import supplyChainService, { SellerSKU } from '../../services/supplyChainService';
import api from '../../services/api';
import { departmentAPI, memberAPI, Member } from '../../services/organizationAPI';
import { SKUMappingTable } from './components/SKUMappingTable';

const SKUMapping: React.FC = () => {
    const navigate = useNavigate();
    const [skus, setSkus] = useState<SellerSKU[]>([]);
    const [members, setMembers] = useState<Member[]>([]);
    const [loading, setLoading] = useState(false);
    const [internalSkus, setInternalSkus] = useState<any[]>([]);

    const fetchData = async () => {
        setLoading(true);
        try {
            const [skuRes, memberRes] = await Promise.all([
                supplyChainService.getSellerSKUs(),
                memberAPI.list()
            ]);

            const skuData = skuRes.data as any;
            setSkus(Array.isArray(skuData) ? skuData : skuData?.results || []);

            const memberData = memberRes.data as any;
            setMembers(Array.isArray(memberData) ? memberData : memberData?.results || []);

        } catch (error) {
            message.error('加载数据失败');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
        api.get('/skus/').then(res => {
            const data = res.data as any;
            setInternalSkus(Array.isArray(data) ? data : data?.results || []);
        });

        // Auto sync on mount
        autoSyncMissingSkus();
    }, []);

    const autoSyncMissingSkus = async () => {
        try {
            const res = await supplyChainService.syncFromOrders();
            if (res.data.success && (res.data.created_count > 0 || res.data.linked_orders_count > 0)) {
                message.success(`自动同步完成: 新增 ${res.data.created_count} 个SKU`);
                fetchData(); // Refresh data if changes occurred
            }
        } catch (e) {
            console.error("Auto sync failed", e);
        }
    };

    const handleAdd = () => {
        navigate('/supply-chain/sku-mapping/new');
    };

    const handleEdit = (record: SellerSKU) => {
        navigate(`/supply-chain/sku-mapping/${record.id}`);
    };

    const handleSync = async () => {
        setLoading(true);
        try {
            const res = await supplyChainService.syncFromOrders();
            if (res.data.success) {
                message.success(res.data.message);
                fetchData();
            }
        } catch (e) {
            message.error('同步失败');
        } finally {
            setLoading(false);
        }
    };

    const handleUpdateOwner = async (id: number, ownerId: number) => {
        try {
            await supplyChainService.updateSellerSKU(id, { owner: ownerId });
            message.success('负责人已更新');
            fetchData();
        } catch (e) {
            message.error('更新负责人失败');
        }
    };

    const handleUpdateMap = async (id: number, internalSkuId: number) => {
        try {
            await supplyChainService.updateSellerSKU(id, { internal_sku: internalSkuId });
            message.success('映射关系已更新');
            fetchData();
        } catch (e) {
            message.error('更新映射失败');
        }
    };

    const handleExport = async () => {
        try {
            const response = await supplyChainService.exportExcel();
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', 'sku_mapping_export.xlsx');
            document.body.appendChild(link);
            link.click();
            link.remove();
            message.success('导出成功');
        } catch (e) {
            message.error('导出失败');
        }
    };

    const handleImport = async (file: File) => {
        try {
            const res = await supplyChainService.batchImport(file);
            message.success(res.data.message);
            fetchData();
            return false; // Prevent default upload behavior
        } catch (e) {
            message.error('导入失败');
            return false;
        }
    };

    return (
        <Card title="SKU映射" extra={
            <div style={{ display: 'flex', gap: '10px' }}>
                <Button onClick={handleExport} icon={<DownloadOutlined />}>导出</Button>
                <Upload
                    beforeUpload={(file: File) => { handleImport(file); return false; }}
                    showUploadList={false}
                    accept=".xlsx,.xls"
                >
                    <Button icon={<UploadOutlined />}>导入</Button>
                </Upload>
                <Button onClick={handleSync} loading={loading}>手动同步缺失SKU</Button>
                <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>添加映射</Button>
            </div>
        }>
            <SKUMappingTable
                dataSource={skus}
                loading={loading}
                onEdit={handleEdit}
                members={members}
                onUpdateOwner={handleUpdateOwner}
                internalSkus={internalSkus}
                onUpdateMap={handleUpdateMap}
            />
        </Card>
    );
};

export default SKUMapping;
